import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, Input, Modal, Select } from '@/components/ui';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const TRIGGER_URL = `${supabaseUrl}/functions/v1/admin-trigger-llm-run`;

const GROUP_OPTIONS = [
  { value: 'baseline', label: 'baseline' },
  { value: 'stress-5', label: 'stress-5' },
  { value: 'stress-10', label: 'stress-10' },
  { value: 'stress-20', label: 'stress-20' },
  { value: 'provider-comparison', label: 'provider-comparison' },
  { value: 'circuit-breaker', label: 'circuit-breaker' },
  { value: 'edge-cases', label: 'edge-cases' },
];

const PROVIDER_OPTIONS = [
  { value: '', label: 'Default (preset)' },
  { value: 'openai', label: 'openai' },
  { value: 'google', label: 'google' },
  { value: 'stub', label: 'stub' },
];

export interface RunStressTestModalProps {
  open: boolean;
  onClose: () => void;
  adminPassword: string;
  onSuccess?: () => void;
}

/**
 * Two-password gate before triggering the costly LLM stress-test workflow.
 * The admin password (X-Admin-Password header) is already known from the
 * shell login; the stress-test password is typed here and sent in the body.
 */
export function RunStressTestModal({
  open,
  onClose,
  adminPassword,
  onSuccess,
}: RunStressTestModalProps) {
  const [stressTestPassword, setStressTestPassword] = useState('');
  const [group, setGroup] = useState('baseline');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [runName, setRunName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset form whenever the modal is reopened so a previous error/success
  // doesn't leak into the next dispatch.
  useEffect(() => {
    if (open) {
      setStressTestPassword('');
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!stressTestPassword) {
      setError('Stress-test password is required');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(TRIGGER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({
          stressTestPassword,
          group,
          provider: provider || undefined,
          model: model || undefined,
          runName: runName || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dispatch failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Run LLM stress test"
      footer={
        success ? (
          <Button
            variant="primary"
            onClick={onClose}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!stressTestPassword || submitting}
            >
              Run
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {success ? (
          <div className="px-4 py-3 bg-success-light border border-success rounded-md text-success text-sm">
            Workflow dispatched. The new run will appear in the list once the
            GitHub Actions job finishes (typically a few minutes).
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2 px-3 py-2 bg-warning-light border border-warning rounded-md text-warning text-xs">
              <AlertTriangle
                size={14}
                className="shrink-0 mt-0.5"
              />
              <span>
                Each run hits real LLM APIs and costs real money. Larger presets
                (stress-20, edge-cases) can run a few dollars.
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-foreground">
                Stress-test password
              </label>
              <Input
                type="password"
                placeholder="2nd password"
                value={stressTestPassword}
                onChange={(e) => setStressTestPassword(e.target.value)}
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-foreground">
                Preset
              </label>
              <Select
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                options={GROUP_OPTIONS}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-foreground">
                  Provider override
                </label>
                <Select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  options={PROVIDER_OPTIONS}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-foreground">
                  Model override
                </label>
                <Input
                  type="text"
                  placeholder="e.g. gpt-4o-mini"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-foreground">
                Run name (optional)
              </label>
              <Input
                type="text"
                placeholder="e.g. pre-release-smoke"
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
              />
            </div>

            {error ? (
              <div className="px-3 py-2 bg-danger-light border border-danger rounded-md text-danger text-sm">
                {error}
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
